import { useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import {
  faDatabase,
  faTriangleExclamation,
  faUserSlash,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import {
  useVideoScanFilterOptions,
  useVideoErrorStats,
  useVideoStatsCases,
} from "../../hooks/useVideoLogs"
import { sourceOptions } from "../../constants"
import ApiError from "../../components/ApiError"
import StatTile from "../../components/StatTile"
import { getDateRange } from "../../utils/datetime"
import NotFoundPhysicianList from "./NotFoundPhysicianList"
import VideoLogsFilterBar from "./VideoLogsFilterBar"

const PAGE_SIZE = 20

const CUSTOM = "Custom"
const DEFAULT_RANGE = "This Week"
const DATE_RANGE_OPTIONS = [
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "All",
]
const DATE_RANGE_PRESETS = DATE_RANGE_OPTIONS.map((value) => ({
  value,
  label: value,
}))

const formatRangeLabel = (startIso, endIso) => {
  if (!startIso || !endIso) return ""
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  if (!start.isValid || !end.isValid) return ""

  const sameMonth = start.hasSame(end, "month") && start.hasSame(end, "year")
  const startLabel = start.toFormat("MMM d")
  const endLabel = sameMonth ? end.toFormat("d") : end.toFormat("MMM d")
  return `${startLabel} – ${endLabel}`
}

const EducationalVideoLogs = () => {
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: DEFAULT_RANGE,
  })
  const [dateFrom, setDateFrom] = useQueryState("dateFrom", {
    defaultValue: "",
  })
  const [dateTo, setDateTo] = useQueryState("dateTo", { defaultValue: "" })
  const [status, setStatus] = useQueryState("status", { defaultValue: "All" })
  const [patientIssuer, setPatientIssuer] = useQueryState("patientIssuer", {
    defaultValue: "All",
  })
  const [source, setSource] = useQueryState("source", { defaultValue: "All" })

  const sentinelRef = useRef(null)

  const isCustomDateRange = dateRange === CUSTOM

  const getDateBounds = () => {
    if (isCustomDateRange) {
      return { start: dateFrom || "", end: dateTo || "" }
    }
    if (dateRange === "All") {
      return { start: "", end: "" }
    }
    // A hand-edited URL can carry a range name that getDateRange would throw on.
    const range = DATE_RANGE_OPTIONS.includes(dateRange)
      ? dateRange
      : DEFAULT_RANGE
    const interval = getDateRange(range)
    return { start: interval.start.toISODate(), end: interval.end.toISODate() }
  }

  const { start, end } = getDateBounds()
  const timePeriod = start || end ? [start, end].join(",") : undefined
  const rangeLabel = formatRangeLabel(start, end)

  const dateRangeTriggerLabel =
    isCustomDateRange && rangeLabel
      ? rangeLabel
      : isCustomDateRange
        ? CUSTOM
        : dateRange

  const handleSelectDateRangePreset = (range) => setDateRange(range)

  const handleApplyCustomDateRange = (from, to) => {
    setDateFrom(from)
    setDateTo(to)
    setDateRange(CUSTOM)
  }

  // Seeds the custom calendar from whatever range is already on screen, so it
  // opens on something meaningful instead of blank.
  const getDateRangeCustomSeed = () => {
    if (isCustomDateRange) return [dateFrom, dateTo]
    if (dateRange === "All") {
      const interval = getDateRange(DEFAULT_RANGE)
      return [interval.start.toISODate(), interval.end.toISODate()]
    }
    const named = DATE_RANGE_OPTIONS.includes(dateRange)
      ? dateRange
      : DEFAULT_RANGE
    const interval = getDateRange(named)
    return [interval.start.toISODate(), interval.end.toISODate()]
  }

  const { data: filterOptions } = useVideoScanFilterOptions()
  const issuerOptions = ["All", ...(filterOptions?.patientIssuers || [])]

  const {
    data: { summary: errorSummary } = {
      summary: {
        totalRecords: 0,
        failedRecords: 0,
        failedDistinctAccessions: 0,
        successDistinctAccessions: 0,
        noRecordRecords: 0,
        uniqueNotFound: 0,
        multipleWith: 0,
        multipleWithout: 0,
      },
    },
  } = useVideoErrorStats({
    limit: 1,
    filters: {
      timePeriod,
      patientIssuer: patientIssuer === "All" ? undefined : patientIssuer,
      source: source === "All" ? undefined : source,
      category: "no_record",
    },
  })

  const {
    data: { categories, groups, total } = {
      categories: [],
      groups: [],
      total: 0,
    },
    isLoading: isPhysiciansLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVideoStatsCases({
    limit: PAGE_SIZE,
    filters: {
      timePeriod,
      patientIssuer: patientIssuer === "All" ? undefined : patientIssuer,
      source: source === "All" ? undefined : source,
      category: "NOT_FOUND",
    },
  })

  const notFoundCount =
    categories.find((c) => c.key === "NOT_FOUND")?.distinctCount || 0

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: "200px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleClearFilters = () => {
    setDateRange(DEFAULT_RANGE)
    setDateFrom("")
    setDateTo("")
    setStatus("All")
    setPatientIssuer("All")
    setSource("All")
  }

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      <h1 className="shrink-0 text-2xl font-bold">
        Educational Video QR Code Analytics
      </h1>

      <div className="shrink-0">
        <VideoLogsFilterBar
          dateRangeCustomLabel={CUSTOM}
          dateRangePresets={DATE_RANGE_PRESETS}
          dateRangeTriggerLabel={dateRangeTriggerLabel}
          dateRangeValue={dateRange}
          issuerOptions={issuerOptions}
          patientIssuer={patientIssuer}
          source={source}
          sourceOptions={["All", ...sourceOptions]}
          status={status}
          onApplyCustomDateRange={handleApplyCustomDateRange}
          onClearFilters={handleClearFilters}
          onGetDateRangeCustomSeed={getDateRangeCustomSeed}
          onPatientIssuer={setPatientIssuer}
          onSelectDateRangePreset={handleSelectDateRangePreset}
          onSource={setSource}
          onStatus={setStatus}
        />
      </div>

      <div className="shrink-0 flex flex-wrap gap-3">
        <StatTile
          accent="green"
          hint="Distinct QR codes (accession numbers) with no matching error in this period"
          icon={faDatabase}
          label="QR Attached"
          value={errorSummary.successDistinctAccessions}
        />
        <StatTile
          accent="red"
          hint="Distinct QR codes (accession numbers) with at least one failed matching record in this period"
          icon={faTriangleExclamation}
          label="QR Failed"
          value={errorSummary.failedDistinctAccessions}
        />
        <StatTile
          accent="amber"
          hint="Distinct QR codes where the referring physician match source is NOT_FOUND"
          icon={faUserSlash}
          label="QR Generic Video Records"
          value={notFoundCount}
        />
        <StatTile
          accent="red"
          hint="Distinct referring physicians with no match found in this period"
          icon={faUsers}
          label="Missing Physicians"
          value={total}
        />
      </div>

      <NotFoundPhysicianList
        groups={groups}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isPhysiciansLoading}
        sentinelRef={sentinelRef}
        total={total}
        onLoadMore={fetchNextPage}
      />
    </div>
  )
}

export default EducationalVideoLogs
